/**
 * Os blocos do texto: o que o documento tem, e o que o documento antigo vira.
 *
 * O corpo da seção era `paragrafos: string[]` mais uma `citacaoLonga`; agora é uma
 * lista de blocos com **tipo**. A troca não pode quebrar o que já está gravado, e é
 * aqui que isso se resolve: `blocosDaSecao` devolve os blocos da seção quando ela
 * tem, e **monta** os blocos a partir dos campos antigos quando não tem. Quem lê o
 * documento chama esta função e não precisa saber de qual das duas formas o texto
 * veio.
 *
 * A volta também vive aqui (`paragrafosDeBlocos`, `citacaoLongaDeBlocos`): ao
 * gravar, os campos antigos continuam sendo escritos como **espelho** dos blocos de
 * parágrafo e de citação. Assim o documento que sobe para o banco tem sempre as
 * duas leituras certas — a nova (blocos) e a antiga (parágrafos) —, e nada do que
 * já estava gravado se perde.
 */
import { LARGURA_DE_IMAGEM_MM, ROTULO_TIPO_DE_BLOCO } from './tipos.js';
/** O bloco tem conteúdo para imprimir? (Bloco vazio não sai no papel.) */
export function blocoTemTexto(bloco) {
    if (bloco.tipo === 'LISTA')
        return (bloco.itens ?? []).some((item) => item.trim());
    if (bloco.tipo === 'TABELA')
        return (bloco.linhas ?? []).some((linha) => linha.some((celula) => celula.trim()));
    /* Imagem sem arquivo não imprime: legenda sozinha não é figura. */
    if (bloco.tipo === 'IMAGEM')
        return Boolean(bloco.url?.trim());
    return Boolean(bloco.texto?.trim());
}
/**
 * O texto do bloco, para quem precisa ler (citação no texto, contagem de palavras,
 * lista de conferência). Lista devolve os itens separados por espaço.
 */
export function textoDoBloco(bloco) {
    if (bloco.tipo === 'LISTA')
        return (bloco.itens ?? []).filter(Boolean).join(' ');
    /*
     * A tabela conta como texto: a citação dentro de uma célula vale para a
     * conferência, e as palavras da tabela contam no limite do plano — senão quem
     * escreve em tabela fugiria do limite sem perceber.
     */
    if (bloco.tipo === 'TABELA') {
        return [...(bloco.linhas ?? []).flat(), bloco.legenda ?? '', bloco.fonte ?? '']
            .filter(Boolean)
            .join(' ');
    }
    /* A legenda e a fonte da figura contam como texto; o arquivo, não. */
    if (bloco.tipo === 'IMAGEM')
        return [bloco.legenda ?? '', bloco.fonte ?? ''].filter(Boolean).join(' ');
    /*
     * A **nota** conta como texto do documento: ela pode trazer uma citação (é para isso que
     * ela serve), e as palavras dela entram na conta do limite do plano.
     */
    return bloco.texto ?? '';
}
/**
 * Os blocos da seção — os blocos dela, ou os que os campos antigos viram.
 *
 * A ordem do documento antigo é a ordem em que ele **imprime**: os parágrafos (na
 * ordem gravada, inclusive os vazios, que no editor são o lugar do cursor) e a
 * citação longa depois deles. Os identificadores são derivados da posição, então
 * eles não mudam entre uma renderização e outra — e o React (e o cursor) ficam
 * quietos.
 */
export function blocosDaSecao(secao) {
    if (secao.blocos && secao.blocos.length > 0)
        return secao.blocos;
    const blocos = secao.paragrafos.map((texto, indice) => ({
        id: `${secao.id}-p${indice}`,
        tipo: 'PARAGRAFO',
        texto,
    }));
    /*
     * A citação longa do campo antigo entra como bloco quando tem texto: um campo
     * vazio não tem o que imprimir, e um bloco vazio na lista só serviria para o
     * cursor — que o editor nunca pôs ali (o campo antigo não era editável no papel).
     */
    if (secao.citacaoLonga?.trim()) {
        blocos.push({ id: `${secao.id}-citacao`, tipo: 'CITACAO', texto: secao.citacaoLonga });
    }
    return blocos;
}
/** Todos os blocos do documento, na ordem em que são impressos. */
export function blocosDoDocumento(documento) {
    return documento.secoes.flatMap((secao) => blocosDaSecao(secao));
}
/**
 * Os parágrafos de um conjunto de blocos — o **espelho** que continua sendo
 * gravado em `paragrafos`.
 *
 * Os blocos vazios entram (é o parágrafo novo, ainda sem texto, onde o cursor
 * está); quem imprime é que os ignora.
 */
export function paragrafosDeBlocos(blocos) {
    return blocos.filter((bloco) => bloco.tipo === 'PARAGRAFO').map((bloco) => bloco.texto ?? '');
}
/**
 * A citação longa vista pelos blocos — o espelho de `citacaoLonga`.
 *
 * O campo antigo guardava a citação inteira, com os parágrafos separados por linha
 * em branco; se o texto tiver mais de um bloco de citação, eles voltam para lá
 * juntos, e nenhum leitor antigo perde pedaço.
 */
export function citacaoLongaDeBlocos(blocos) {
    return blocos
        .filter((bloco) => bloco.tipo === 'CITACAO')
        .map((bloco) => bloco.texto ?? '')
        .filter((texto) => texto.trim())
        .join('\n\n');
}
/**
 * Um bloco novo, vazio, do tipo pedido.
 *
 * A lista já nasce com um item — uma lista sem item nenhum não teria onde o cursor
 * entrar, e o editor não teria o que desenhar.
 */
export function blocoNovo(tipo, id) {
    if (tipo === 'LISTA')
        return { id, tipo, lista: 'MARCADA', itens: [''] };
    /*
     * A tabela nasce 2×2 (cabeçalho + uma linha), que é o menor quadro em que se
     * consegue escrever alguma coisa — e a pessoa acrescenta linhas e colunas depois.
     */
    if (tipo === 'TABELA') {
        return {
            id,
            tipo,
            legenda: '',
            fonte: '',
            linhas: [
                ['', ''],
                ['', ''],
            ],
        };
    }
    if (tipo === 'IMAGEM')
        return { id, tipo, url: '', legenda: '', fonte: '', larguraMm: LARGURA_DE_IMAGEM_MM };
    return { id, tipo, texto: '' };
}
/**
 * A largura impressa da figura: a escolhida, ou a da mancha quando ninguém escolheu.
 *
 * A medida mora no bloco porque cada figura tem a sua — o gráfico estreito não precisa
 * ocupar a largura toda só porque a foto da capa ocupa.
 */
export function larguraDaImagem(bloco, larguraDaManchaMm) {
    const medida = bloco.larguraMm;
    const pedida = Number.isFinite(medida ?? NaN) && (medida ?? 0) > 0 ? medida : LARGURA_DE_IMAGEM_MM;
    /*
     * O teto é a **mancha** da folha: figura mais larga que o espaço de texto sairia por cima
     * da margem. Quem sabe a largura da mancha é quem desenha (o papel), e por isso ela chega
     * como argumento — o motor dos blocos não conhece a folha.
     */
    const teto = larguraDaManchaMm && larguraDaManchaMm > 0 ? larguraDaManchaMm : LARGURA_DE_IMAGEM_MM;
    return Math.max(10, Math.min(teto, pedida));
}
/**
 * O número de cada **nota**, na ordem em que ela aparece no documento.
 *
 * A conta é do motor e não do bloco (o bloco não guarda número): quem manda é a posição —
 * mover uma nota de lugar renumera as outras, e é isso que a leitura espera. Devolve o mapa
 * pelo `id` do bloco, que é o que o sumário e o editor têm à mão.
 */
export function numerosDasNotas(documento) {
    const numeros = new Map();
    let contador = 0;
    for (const bloco of blocosDoDocumento(documento)) {
        if (bloco.tipo !== 'NOTA')
            continue;
        contador += 1;
        numeros.set(bloco.id, contador);
    }
    return numeros;
}
/**
 * O número de cada **figura**, na ordem em que ela aparece no documento.
 *
 * A conta é do motor e não do bloco nem da lista (a figura não guarda número): quem
 * manda é a **posição de leitura** — mover uma figura de lugar renumera as outras, e é
 * isso que o leitor espera ("Figura 1" é a primeira que ele vê).
 *
 * Havia **duas fontes de figura** e cada uma contava do zero: a figura do texto (bloco
 * `IMAGEM`, com um contador que zerava a cada seção) e a figura da lista avulsa
 * (`documento.figuras`, numerada pelo índice). Com uma de cada, o documento saía com
 * **duas "Figura 1"**. Aqui as duas entram no mesmo contador, na ordem em que saem no
 * papel: primeiro as figuras dos blocos, seção por seção; depois as da lista avulsa, que
 * a impressão desenha no fim do texto. A lista de figuras e o corpo leem este mesmo mapa,
 * então o número do sumário é o número da figura.
 *
 * Devolve o mapa pelo `id` (do bloco ou da figura), que é o que quem desenha tem à mão.
 */
export function numerosDasFiguras(documento) {
    const numeros = new Map();
    let contador = 0;
    /* As figuras do texto, na ordem das seções e dos blocos. */
    for (const bloco of blocosDoDocumento(documento)) {
        if (bloco.tipo !== 'IMAGEM')
            continue;
        /* Figura sem arquivo não imprime, então não consome número (como `blocoTemTexto`). */
        if (!bloco.url?.trim())
            continue;
        contador += 1;
        numeros.set(bloco.id, contador);
    }
    /* As figuras da lista avulsa, na ordem em que a impressão as desenha (depois do texto). */
    for (const figura of documento.figuras) {
        contador += 1;
        numeros.set(figura.id, contador);
    }
    return numeros;
}
/**
 * O conjunto com um bloco mudado de lugar — o que faz o "sobe"/"desce" do editor.
 *
 * Devolve o mesmo conjunto quando o destino não existe (mover o primeiro para cima,
 * por exemplo): quem chama não precisa conferir as pontas.
 */
export function moverBloco(blocos, de, para) {
    if (de === para || de < 0 || para < 0 || de >= blocos.length || para >= blocos.length)
        return blocos;
    const copia = [...blocos];
    const [bloco] = copia.splice(de, 1);
    if (!bloco)
        return blocos;
    copia.splice(para, 0, bloco);
    return copia;
}
/** Como o bloco se chama na tela (o editor usa no menu e na lista do painel). */
export function rotuloDoBloco(bloco) {
    return ROTULO_TIPO_DE_BLOCO[bloco.tipo];
}
/**
 * A tabela é retangular: toda linha tem o mesmo número de colunas.
 *
 * O editor de tabela acrescenta coluna em uma linha de cada vez (é assim que o HTML
 * funciona), e uma linha mais curta que as outras faz a régua do papel sair torta. O
 * motor completa o que falta com célula vazia, e corta o que sobra.
 */
export function tabelaRetangular(linhas) {
    const colunas = linhas.reduce((maior, linha) => Math.max(maior, linha.length), 0);
    return linhas.map((linha) => Array.from({ length: colunas }, (_, indice) => linha[indice] ?? ''));
}
/** Acrescenta uma linha (ou uma coluna) à tabela, mantendo-a retangular. */
export function acrescentarLinha(linhas, onde) {
    const atual = tabelaRetangular(linhas);
    if (onde === 'LINHA')
        return [...atual, Array.from({ length: atual[0]?.length ?? 1 }, () => '')];
    return atual.map((linha) => [...linha, '']);
}
/** Tira a última linha (ou a última coluna), sem deixar a tabela vazia. */
export function tirarLinha(linhas, onde) {
    const atual = tabelaRetangular(linhas);
    if (onde === 'LINHA')
        return atual.length > 1 ? atual.slice(0, -1) : atual;
    const colunas = atual[0]?.length ?? 0;
    return colunas > 1 ? atual.map((linha) => linha.slice(0, -1)) : atual;
}
/**
 * Um resumo curto do bloco, para a lista do painel: o começo do texto, ou
 * "n itens" quando é lista.
 */
export function resumoDoBloco(bloco) {
    if (bloco.tipo === 'LISTA') {
        const itens = (bloco.itens ?? []).filter((item) => item.trim()).length;
        return `${itens} ${itens === 1 ? 'item' : 'itens'}`;
    }
    if (bloco.tipo === 'TABELA') {
        const linhas = bloco.linhas ?? [];
        const colunas = linhas[0]?.length ?? 0;
        return `${linhas.length} × ${colunas}`;
    }
    if (bloco.tipo === 'IMAGEM') {
        if (!bloco.url?.trim())
            return 'sem arquivo';
        return bloco.legenda?.trim() ? `figura: ${bloco.legenda.trim()}` : 'figura';
    }
    const texto = (bloco.texto ?? '').replace(/\s+/g, ' ').trim();
    if (!texto)
        return 'em branco';
    return texto.length > 60 ? `${texto.slice(0, 60)}...` : texto;
}
//# sourceMappingURL=blocos.js.map